import {
  AspectRatio,
  ButtonGroup,
  Center,
  Flex,
  IconButton,
  Image as ChakraImage,
  Stack,
  Switch,
  Text as ChakraText,
  useDisclosure,
} from '@chakra-ui/react';
import { DeleteIcon, PencilIcon } from '@diamond/shared/icons';
import { Heading, Modal, Text } from '@diamond/shared/ui';
import React, { useMemo } from 'react';

import type { VoucherListItem, VoucherStatus } from '../types';
import { columnWidths } from './columns-width';
import { VoucherPublicationStatusPill } from './voucher-publication-status-pill';

const statusLabelMap: Record<VoucherStatus | string, string> = {
  ACTIVE: 'Aktif',
  INACTIVE: 'Tidak Aktif',
  EXPIRED: 'Kedaluwarsa',
};

function isSnapshotLockedStatus(status: VoucherStatus): boolean {
  return status === 'PENDING' || status === 'FAILED';
}

function isPendingStatus(status: VoucherStatus): boolean {
  return status === 'PENDING';
}

function activeCaption(status: VoucherStatus): string {
  if (isSnapshotLockedStatus(status)) {
    return 'Tidak Aktif';
  }
  return statusLabelMap[status] ?? status;
}

interface VoucherItemProps {
  item: VoucherListItem;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onToggleStatus: (id: string, status: VoucherStatus) => void;
  onToggleDisplay: (id: string, isDisplay: boolean) => void;
  isDeleteLoading?: boolean;
  isStatusLoading?: boolean;
  isDisplayLoading?: boolean;
}

function formatPeriod(periodStart: number, periodEnd: number) {
  if (!periodStart || !periodEnd) return '-';

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  };
  const formatter = new Intl.DateTimeFormat('id-ID', dateOptions);

  return `${formatter.format(
    new Date(periodStart * 1000)
  )} - ${formatter.format(new Date(periodEnd * 1000))}`;
}

function formatMechanism(item: VoucherListItem) {
  const { reward_type, discount_products, free_goods } =
    item.rewardConfiguration;

  switch (reward_type) {
    case 'DISCOUNT_PERCENT': {
      const percentage = discount_products?.[0]?.discount_percentage ?? 0;
      return { title: 'Diskon %', description: `${percentage}%` };
    }
    case 'DISCOUNT_AMOUNT': {
      const amount = discount_products?.[0]?.discount_amount ?? 0;
      const formattedAmount = new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        maximumFractionDigits: 0,
      }).format(amount);
      return { title: 'Diskon Nominal', description: formattedAmount };
    }
    case 'FREE_GOODS': {
      const quantity = free_goods?.[0]?.quantity ?? 0;
      return {
        title: 'Free Goods',
        description: `Qty ${quantity}`,
      };
    }
    default:
      return { title: reward_type, description: '-' };
  }
}

const formatSegment = (items: string[]) => {
  if (!items || items.length === 0) {
    return '-';
  }

  if (items.length === 1) {
    return items[0];
  }

  return `${items[0]}, +${items.length - 1} lainnya`;
};

const formatBranches = (item: VoucherListItem) => {
  const { distribution_channels } = item.targetConfiguration;

  if (distribution_channels.is_all) {
    return 'Semua Cabang';
  }

  if (distribution_channels.count > 0) {
    return `${distribution_channels.count} Cabang`;
  }

  if (distribution_channels.items.length > 0) {
    return formatSegment(distribution_channels.items);
  }

  return '-';
};

const formatUsage = (item: VoucherListItem) => {
  const { quota_total, quota_used } = item.targetConfiguration;

  if (!quota_total) {
    return '-';
  }

  return `${quota_used}/${quota_total}`;
};

function VoucherItemComponent({
  item,
  onDelete,
  onEdit,
  onDuplicate: _onDuplicate,
  onToggleStatus,
  onToggleDisplay,
  isDeleteLoading,
  isStatusLoading,
  isDisplayLoading,
}: VoucherItemProps) {
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const handleEdit = () => onEdit(item.id);
  const handleDeleteClick = () => onDeleteOpen();

  const periodLabel = useMemo(
    () => formatPeriod(item.periodStart, item.periodEnd),
    [item.periodStart, item.periodEnd]
  );

  const mechanism = useMemo(() => formatMechanism(item), [item]);
  const branchesLabel = useMemo(() => formatBranches(item), [item]);
  const cg1Label = useMemo(
    () => formatSegment(item.targetConfiguration.cg1_business_segmentations),
    [item.targetConfiguration.cg1_business_segmentations]
  );
  const cg2Label = useMemo(
    () => formatSegment(item.targetConfiguration.cg2_business_categories),
    [item.targetConfiguration.cg2_business_categories]
  );
  const usageLabel = useMemo(() => formatUsage(item), [item]);

  const handleStatusToggle = () => {
    if (isSnapshotLockedStatus(item.status)) {
      return;
    }
    const nextStatus: VoucherStatus =
      item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    onToggleStatus(item.id, nextStatus);
  };

  const handleDisplayToggle = () => {
    onToggleDisplay(item.id, !item.isDisplay);
  };

  return (
    <Flex
      w="full"
      alignItems="center"
      borderColor="gray.200"
      px={1}
      py={3}
      _hover={{ bg: 'gray.50' }}
      minH="92px"
    >
      {/* <Flex w={columnWidths.dragHandle} justify="center" /> */}

      <Flex w={columnWidths.thumbnail} justify="center">
        <AspectRatio ratio={16 / 9} w="full" maxW="120px">
          <ChakraImage
            src={
              item.bannerUrl ||
              'https://placehold.co/280x120?text=Voucher+Banner'
            }
            alt={item.code}
            objectFit="cover"
            borderRadius="lg"
          />
        </AspectRatio>
      </Flex>

      <Stack w={columnWidths.code} spacing={1} justify="center">
        <Text fontWeight="semibold" align="center" fontSize="sm">
          {item.code}
        </Text>
        <Text fontSize="xs" color="gray.600" align="center">
          {periodLabel}
        </Text>
      </Stack>

      <Stack w={columnWidths.mechanism} spacing={1} align="center">
        <Text fontSize="sm" fontWeight="medium" textAlign="center">
          {mechanism.title}
        </Text>
        <Text fontSize="xs" color="gray.600" textAlign="center">
          {mechanism.description}
        </Text>
      </Stack>

      <Flex w={columnWidths.branch} justify="center" align="center">
        <Text fontSize="sm" textAlign="center">
          {branchesLabel}
        </Text>
      </Flex>

      <Flex w={columnWidths.cg1} justify="center" align="center">
        <Text fontSize="sm" textAlign="center">
          {cg1Label}
        </Text>
      </Flex>

      <Flex w={columnWidths.cg2} justify="center" align="center">
        <Text fontSize="sm" textAlign="center">
          {cg2Label}
        </Text>
      </Flex>

      <Flex w={columnWidths.usage} justify="center" align="center">
        <Text fontSize="sm" textAlign="center">
          {usageLabel}
        </Text>
      </Flex>

      <Stack w={columnWidths.activeStatus} align="center" spacing={1}>
        <Switch
          isChecked={item.status === 'ACTIVE'}
          size="md"
          bgColor="white"
          colorScheme="linkedin"
          data-testid="voucher-status"
          onChange={handleStatusToggle}
          isDisabled={isSnapshotLockedStatus(item.status) || isStatusLoading}
        />
        <ChakraText fontSize="xs" color="gray.600">
          {activeCaption(item.status)}
        </ChakraText>
      </Stack>

      <Stack w={columnWidths.displayStatus} align="center" spacing={1}>
        <Switch
          isChecked={item.isDisplay}
          size="md"
          bgColor="white"
          colorScheme="linkedin"
          data-testid="voucher-display"
          onChange={handleDisplayToggle}
          isDisabled={isSnapshotLockedStatus(item.status) || isDisplayLoading}
        />
        <ChakraText fontSize="xs" color="gray.600">
          {item.isDisplay ? 'Ya' : 'Tidak'}
        </ChakraText>
      </Stack>

      <Flex w={columnWidths.publicationStatus} justify="center" align="center">
        <VoucherPublicationStatusPill status={item.status} />
      </Flex>

      <Flex w={columnWidths.actions} justify="center" align="center">
        <ButtonGroup spacing={0.5}>
          <IconButton
            aria-label="Edit voucher"
            icon={<PencilIcon />}
            size="sm"
            variant="ghost"
            color="blue.500"
            _hover={{ bg: 'blue.50' }}
            onClick={handleEdit}
            isDisabled={isPendingStatus(item.status)}
          />
          <IconButton
            aria-label="Delete voucher"
            icon={<DeleteIcon />}
            size="sm"
            variant="ghost"
            color="red.500"
            _hover={{ bg: 'red.50' }}
            onClick={handleDeleteClick}
            isDisabled={isPendingStatus(item.status) || isDeleteLoading}
          />
        </ButtonGroup>
      </Flex>

      <Modal
        name="delete-voucher"
        labelSubmit="Hapus"
        labelCancel="Kembali"
        buttonColor="red"
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onSubmit={() => onDelete(item.id)}
        isLoading={isDeleteLoading}
        isCentered
      >
        <Center py={4}>
          <Heading as="h3">Hapus Voucher?</Heading>
        </Center>
      </Modal>
    </Flex>
  );
}

export const VoucherItem = React.memo(VoucherItemComponent);
